import 'core-js/stable'
import 'phoenix_html'
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';
import Alpine from 'alpinejs'
import Clipboard from 'clipboard'
import Fuse from 'fuse.js'
import { LiveSocket } from 'phoenix_live_view'
import { Socket } from 'phoenix'

// Internal
import Map from './alpinejs/map'
import { updateTimeTags } from './hooks/TimeFormat'
import { load } from './analytics'
import Hooks, { bind } from './hooks/Hooks'
import Uploaders from './Uploaders'
import navigation from './navigation'

if (typeof window.queueMicrotask !== 'function') {
  window.queueMicrotask = function queueMicrotask(callback) {
    Promise.resolve()
      .then(callback)
      .catch((e) => setTimeout(() => {
        throw e
      }))
  }
}

window.addEventListener('clip:copy', (event) => Clipboard.copy(event.target.dataset.clipboardText))
window.addEventListener('tpn:remove', (event) => event.target.remove())
window.addEventListener('phx:remove-element', (event) => {
  const element = document.getElementById(event.detail.id)

  if (element) {
    element.remove()
  }
})

window.addEventListener('phx:push-datalayer-event', ({ detail: detail }) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(detail)
})

window.addEventListener('phx:scroll-into-view', (event) => {
  let target = event.target

  if (event.detail.to) {
    target = document.getElementById(event.detail.to)
  }

  if (target) {
    target.scrollIntoView(false)
  }
})

window.addEventListener('phx:scroll-to-top', (event) => {
  const element = document.getElementById(event.detail.id)

  if (element) {
    element.scrollTo(0, 0)
  } else {
    window.scrollTo(0, 0)
  }
})

window.addEventListener('phx:scroll-to-bottom', (event) => {
  const element = document.getElementById(event.detail.dispatcher.id)

  if (element) {
    requestAnimationFrame(() => {
      element.scrollTop = element.scrollHeight;
    });
  }
})

window.addEventListener('phx:clear-textarea', (event) => {
  const element = document.getElementById(event.detail.id);

  if (element && element.value.trim() !== '') {
    element.value = '';
  }
});

window.addEventListener('phx:chat-scroll-near-bottom', (event) => {
  const target = event.detail.dispatcher;
  const scrollableContainer = target.closest('.overflow-y-auto');

  if (scrollableContainer) {
    const THRESHOLD = 250;
    const scrollPosition = scrollableContainer.scrollTop + scrollableContainer.clientHeight;
    const scrollHeight = scrollableContainer.scrollHeight;

    if (scrollHeight - scrollPosition <= THRESHOLD) {
      target.scrollIntoView(false);
    }
  }
});

window.addEventListener('phx:scroll-near-bottom', (event) => {
  const target = event.target
  const parent = target.parentNode

  if ((parent.scrollHeight - (parent.scrollTop + parent.clientHeight + target.clientHeight + 10)) <= 0) {
    event.target.scrollIntoView(false)
  }
})

window.addEventListener('phx:show-ping', (e) => {
  document.querySelectorAll('[data-show-ping]').forEach((element) => {
    const parentElement = document.querySelector('#sitewide-chat');
    const childElement = document.activeElement

    const isActive = (parentElement.contains(childElement));

    if (element.id === e.detail.id && (!isActive || e.detail.inactive_unread_messages)) {
      liveSocket.execJS(element, element.getAttribute('data-show-ping'))
    }
  })
})

window.addEventListener('phx:hide-ping', (e) => {
  document.querySelectorAll('[data-hide-ping]').forEach((element) => {
    if (element.id === e.detail.id) {
      liveSocket.execJS(element, element.getAttribute('data-hide-ping'))
    }
  })
})

function alpinejs() {
  window.Alpine = Alpine

  Alpine.data('map', () => Map.create())

  Alpine.data('modal', () => ({
    init() {
      this.$el.focus()
    },
  }))

  Alpine.data('merge', (...dataSets) => {
    return dataSets.reduce((data, dataSet) => {
      return { ...data, ...dataSet }
    }, {})
  })

  Alpine.data('multiSelect', ({ selected, filtered }) => ({
    dropdown: false,
    selected: [...selected],
    filteredOptions: filtered,
    focused: false,
    focusedOption: filtered[0],
    query: '',
    init() {
      this.allOptions = new Fuse([...selected, ...filtered], {
        keys: ['name'],
        threshold: 0.3,
        location: 0,
        distance: 100,
        includeMatches: true,
        includeScore: true,
        useExtendedSearch: true,
      })

      this.$watch('query', (query) => {
        this.refilterOptions();

        if (query) {
          this.dropdown = true;
        }
      })

      this.$watch('dropdown', (dropdown) => {
        if (dropdown) {
          this.$nextTick(() => this.scroll())
        } else {
          this.clear()
        }
      })
    },
    onClickSelect(dispatch, option, field) {
      this.selectItem(option)
      this.change(dispatch, field)
      this.focus()
    },
    onClickDeselect(dispatch, option, field) {
      this.deselectItem(option)
      this.change(dispatch, field)
      this.focus()
    },
    selectItem(item) {
      this.selected = [...this.selected, item]
      this.query = ''
      this.dropdown = false
      this.refilterOptions()
    },
    deselectItem(item) {
      this.selected = this.selected.filter((opt) => opt.id !== item.id)
      this.dropdown = false
      this.refilterOptions()
    },
    // eslint-disable-next-line complexity
    handleKeydown(e, dispatch, field) {
      switch (e.key) {
      case 'Enter':
      case 'Tab':
        if (this.dropdown && this.focusedOption) {
          e.preventDefault()
          this.selectItem(this.focusedOption)
          this.change(dispatch, field)
        }
        this.dropdown = false
        break
      case 'Backspace':
        if (!this.query && this.selected.length) {
          this.selected = this.selected.slice(0, -1)
          this.refilterOptions()
          this.change(dispatch, field)
        }
        break
      case 'ArrowUp':
        if (this.dropdown) {
          this.shiftFocusedOption(-1)
          this.scroll()
        } else {
          this.dropdown = true
          this.focusedOption = this.filteredOptions[this.filteredOptions.length - 1]
        }
        break
      case 'ArrowDown':
        if (this.dropdown) {
          this.shiftFocusedOption(1)
          this.scroll()
        } else {
          this.dropdown = true
          this.focusedOption = this.filteredOptions[0]
        }
        break
      default:
        return
      }
    },
    resetFocusedOption() {
      this.focusedOption = this.filteredOptions[0]
    },
    updateFocusedOption(option) {
      this.focusedOption = option
    },
    shiftFocusedOption(direction) {
      const currentIndex = this.filteredOptions.map((opt) => opt.id).indexOf(this.focusedOption.id)

      let index = currentIndex + direction

      if (index < 0) {
        index = this.filteredOptions.length - 1
      }

      if (index >= this.filteredOptions.length) {
        index = 0
      }

      this.focusedOption = this.filteredOptions[index]
    },
    refilterOptions() {
      const selections = this.selected.map((item) => item.id)

      if (this.query) {
        this.filteredOptions = this.allOptions
          .search(this.query)
          .map((item) => item.item)
          .filter((item) => !selections.includes(item.id))

      } else {
        this.filteredOptions = this.allOptions
          .getIndex()
          .docs
          .filter((item) => !selections.includes(item.id))
      }

      this.resetFocusedOption()
    },
    focus() {
      this.$refs.input.focus()
    },
    toggleDropdown() {
      if (!this.dropdown) {
        this.resetFocusedOption()
      }

      this.dropdown = true
    },
    // eslint-disable-next-line complexity
    scroll() {
      if (this.focusedOption) {
        const menuEl = this.$refs.menu
        const menuRect = menuEl.getBoundingClientRect()

        const focusedEl = menuEl.querySelector(`[data-option-id='${this.focusedOption.id}']`)

        if (focusedEl) {
          const focusedRect = focusedEl.getBoundingClientRect()
          const overScroll = focusedEl.offsetHeight / 3

          if (focusedRect.bottom + overScroll > menuRect.bottom) {
            menuEl.scrollTop = Math.min(
              focusedEl.offsetTop +
              focusedEl.clientHeight -
              menuEl.offsetHeight +
              overScroll,
              menuEl.scrollHeight,
            )
          } else if (focusedRect.top - overScroll < menuRect.top) {
            menuEl.scrollTop = Math.max(focusedEl.offsetTop - overScroll, 0)
          }
        }
      }
    },
    clearSelections(dispatch, field) {
      this.selected = []
      this.filteredOptions = this.allOptions
      this.resetFocusedOption()
      this.dropdown = false
      this.query = ''
      this.change(dispatch, field)
    },
    clear() {
      this.resetFocusedOption()
      this.query = ''
      this.refilterOptions()
    },
    change(dispatch, field) {
      dispatch('push-event', {
        event: 'validate_multi_select',
        payload: {
          selections: this.selected,
          name: field,
        },
      })
    },
  }))

  Alpine.start()
}

function liveview() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')
  const liveSocket = new LiveSocket('/live', Socket, {
    dom: {
      // eslint-disable-next-line complexity
      onBeforeElUpdated(from, toNode) {
        if (from._x_dataStack) {
          window.Alpine.clone(from, toNode)
        }

        // fix for prepend optimizations that remove alpine init
        if (from.getAttribute('phx-update') && from.getAttribute('phx-update') !== 'ignore') {
          Alpine.initTree(toNode)
        }

        // fix for syncing assigns
        if (from.getAttribute('x-data-sync') === 'true') {
          Alpine.initTree(toNode)
        }
      },
    },
    longPollFallbackMs: 2500,
    params: {
      // eslint-disable-next-line camelcase
      _csrf_token: csrfToken && csrfToken.getAttribute('content'),
    },
    metadata: {
      keydown: (event, _element) => ({ key: event.key, metaKey: event.metaKey, repeat: event.repeat }),
    },
    hooks: Hooks,
    uploaders: Uploaders,
  })

  bind()

  liveSocket.connect()

  window.liveSocket = liveSocket
}

window.addEventListener('phx:live_reload:attached', ({ detail: reloader }) => {
  reloader.enableServerLogs()
})

document.addEventListener('DOMContentLoaded', () => {
  load()
  updateTimeTags()
  navigation.load()

  alpinejs()
  liveview()
})

document.addEventListener('phx:update', () => {
  updateTimeTags()
})
